import { myTeamsStore, guideStore, authStore } from '@stores'
import { isNumber } from '@helpers/utils.js'
import { GAME_ID } from '@config/game.js'
import { computed } from 'vue'
import {
  TEAM_MEMBER_LEVEL_ADMINISTRATOR,
  TEAM_MEMBER_LEVEL_MODERATOR,
  TEAM_MEMBER_LEVEL_MANAGER,
} from '@config/options.js'

export default function () {
  const { myTeams, removeTeam } = myTeamsStore()

  const isGameModerator = computed(() =>
    myTeams.value.some(
      (g) =>
        g.game_id === GAME_ID &&
        (!g.access || g.access.invite_pending === 0) &&
        (!g.resource_type || g.resource_type === 'game')
    )
  )

  /**
   * @param {{
   *  resource: String,
   *  nameId: String,
   *  scope: 'leader' | 'admin' | 'manager' | 'moderator'
   * }} options
   */
  function hasPermissionByNameId({ resource, nameId, scope }) {
    if (!resource || !nameId) {
      return false
    }

    const item = _getAccess({ resource, id: nameId })
    return !!item && _hasAccess(item, scope)
  }

  function hasPermissionForGame() {
    return isGameModerator.value
  }

  /**
   * @param {string} modNameId
   * @param @ignore {string} _gameNameId: String
   * @param {'leader' | 'admin' | 'manager' | 'moderator'} scope
   */
  function hasPermissionForMod(modNameId, _gameNameId, scope) {
    // gameNameId is unused but kept to maintain compatibility with the frontend
    return (
      isGameModerator.value ||
      hasPermissionByNameId({ resource: 'mod', nameId: modNameId, scope })
    )
  }

  /**
   * @param {string} collectionNameId
   * @param {string} gameNameId
   */
  function hasPermissionForCollection(collectionNameId = '', gameNameId = '') {
    // gameNameId is unused but kept to maintain compatibility with the frontend
    // TODO implement when collections add/edit
    return isGameModerator.value
  }

  function hasPermissionForGuideComputed(gameNameId, guideNameId) {
    const { getGuide } = guideStore()
    const { getUserId } = authStore()
    return computed(() => {
      const guide = getGuide(gameNameId, guideNameId)
      return (
        isGameModerator.value || guide.value?.submitted_by?.id === getUserId()
      )
    })
  }

  function removeAccessByNameId({ resource, nameId }) {
    const team = myTeams.value.find((t) => t.name_id === nameId)
    if (team) removeTeam({ resource, id: team.resource_id })
  }

  function _hasAccess(item, scope) {
    if (scope === 'leader') {
      return !!item?.access?.is_leader
    }

    if (scope === 'moderator') scope = TEAM_MEMBER_LEVEL_MODERATOR
    else if (scope === 'manager') scope = TEAM_MEMBER_LEVEL_MANAGER
    else if (scope === 'admin') scope = TEAM_MEMBER_LEVEL_ADMINISTRATOR
    else throw new Error('Invalid scope')

    return item?.access?.level >= scope
  }

  function _getAccess({ resource, id }) {
    const key = isNumber(id) ? 'resource_id' : 'name_id'

    return (
      myTeams.value &&
      myTeams.value.find((item) => {
        return (
          item[key] == id &&
          item.resource_type === resource &&
          item.access?.invite_pending === 0
        )
      })
    )
  }

  function isApiPermissionError(errorCode) {
    return errorCode === 403
  }

  return {
    hasPermissionForGuideComputed,
    hasPermissionForCollection,
    hasPermissionByNameId,
    removeAccessByNameId,
    isApiPermissionError,
    hasPermissionForGame,
    hasPermissionForMod,
    isGameModerator,
  }
}
